// =============================================================================
// CONNECT 2.0 — List screens (Spec 4) nested data model + grouping transform.
// -----------------------------------------------------------------------------
// The three list screens (Invitations, Pending, Active) fetch a FLAT project
// shape from `ProjectService` (`/project/user-projects`):
//
//   Project {
//     projectId, projectOrderId, rush, specialQuoteRequirements, tableA, ...
//     sites: SiteDetail[]              // each = a site+proposal pairing
//   }
//   SiteDetail { site, proposal, reviewStatus }
//
// Each `SiteDetail.site` carries BOTH physical-site fields (siteNumber, parcels
// / address, totalAcreage, landUse, ...) AND the per-service fields
// (siteType + siteSubType = the service, i.e. the *product*). A single physical
// site (shared `siteNumber`) can therefore appear as several `SiteDetail` rows,
// one per service/product, which the 1.0 info-table rendered as separate rows.
//
// CONNECT 2.0 renders the real hierarchy — Project → Site → Product — so this
// module groups the flat `SiteDetail[]` by physical site (`siteNumber`) into a
// nested view-model consumed by `<app-project-group>`. Pure functions, no
// Angular dependency, so all three pages reuse them and they stay unit-testable.
//
// Grouping key note: sites are keyed by `siteNumber`. Products at the same
// physical location share a `siteNumber` and collapse into one site card; if
// `siteNumber` is absent we fall back to the site `id`, which degrades to the
// 1.0 behaviour of one product per card (never lossy, never mis-merged).
// =============================================================================

/**
 * A single product line-item — the atomic actionable unit. Wraps one original
 * `SiteDetail` (kept intact on `siteDetail` so Task 2's action writes can pass
 * the untouched proposal/site objects to `ProposalService`/`SiteService`).
 */
export interface ProductVm {
  /** The original, untouched `{ site, proposal, reviewStatus }` row. */
  siteDetail: any;
  /** Convenience alias for `siteDetail.site`. */
  site: any;
  /** Convenience alias for `siteDetail.proposal`. */
  proposal: any;
  /** Convenience alias for `siteDetail.reviewStatus`. */
  reviewStatus: string;
  /**
   * Convenience alias for `siteDetail.siteVisit` — the narrow site-visit
   * summary the feed attaches when the product has a `SiteVisitSchedule`, and
   * `null` when it has none. Optional so fixtures that predate it still satisfy
   * the interface.
   */
  siteVisit?: any;
  /** Raw proposal status, e.g. 'INVITATION', 'BID_WON', 'ACTIVE'. */
  status: string;
  /** Display title: `siteType - siteSubType` (or just `siteType`). */
  title: string;
}

/**
 * A physical site — shared context (address, acreage, land use, ...) plus the
 * products ordered against it. The representative `site` object is taken from
 * the first product in the group; site-level fields are identical across a
 * group's products.
 */
export interface SiteVm {
  /** Stable grouping key (`num:<siteNumber>` or `id:<site.id>`). */
  key: string;
  /** Physical site number, or null when the source omitted it. */
  siteNumber: number | null;
  /** Representative raw site object (site-level fields live here). */
  site: any;
  /** Products (service line-items) ordered against this site. */
  products: ProductVm[];
}

/**
 * A project — banner context plus its sites. `raw` retains the original,
 * untouched project object for banner/detail fields that later tasks surface
 * (Task 3 banner, ATP stamp, completion %, etc.).
 */
export interface ProjectVm {
  /** The original, untouched project object. */
  raw: any;
  projectId: string;
  projectOrderId: string;
  rush: boolean;
  specialQuoteRequirements: string;
  /** Project-level Table A payload (formatting deferred to Task 4). */
  tableA: any;
  sites: SiteVm[];
}

/**
 * Normalize a raw `/project/user-projects` emission before a page applies its
 * status filter. Drops nullish project entries and any nullish site rows, and
 * coerces a missing `sites`/top-level array to `[]`, so a single malformed
 * element can't throw `Cannot read properties of undefined (reading 'sites')`
 * in a page's `.map((parent) => parent.sites.filter(...))` during change
 * detection. `ProjectService.fetch` already strips holes at the source; this is
 * the shared page-level backstop that also covers post-action merge emissions.
 * Returns shallow-cloned parents (each with a fresh, hole-free `sites` array),
 * safe for the caller to `.map`/`.filter` without mutating the service's cache.
 */
export function sanitizeProjectDetails(result: any): any[] {
  return (Array.isArray(result) ? result : [])
    .filter((parent: any) => !!parent)
    .map((parent: any) => ({
      ...parent,
      sites: (Array.isArray(parent.sites) ? parent.sites : []).filter(
        (site: any) => !!site
      ),
    }));
}

/** Build a product's display title from its site's service type + subtype. */
export function buildProductTitle(site: any): string {
  const type = site?.siteType ?? '';
  const subType = site?.siteSubType;
  return subType ? `${type} - ${subType}` : type;
}

/**
 * Stable grouping key for a physical site.
 *
 * Primary key is `siteNumber` (the intended physical-site identifier). If it is
 * ever missing (a rare data gap the Product side flagged as possible), fall back
 * to the physical address so a site's products still aggregate into one card /
 * workspace instead of fragmenting to one-per-product. The address fallback
 * requires a real STREET (city/state/zip alone could merge distinct sites), and
 * since grouping runs per-project (`toProjectVm`) same-street products are the
 * same physical site. With no `siteNumber` and no street, fall back to the
 * per-product `site.id` — degrades to today's behavior, never mis-merges.
 */
function siteGroupKey(site: any): string {
  const num = site?.siteNumber;
  if (num !== undefined && num !== null && `${num}` !== '') {
    return `num:${num}`;
  }
  const parcel = Array.isArray(site?.parcels) ? site.parcels[0] : null;
  const street = (parcel?.street ?? '').toString().trim().toLowerCase();
  if (street) {
    const addrKey = [street, parcel?.city, parcel?.state, parcel?.zipcode]
      .map((part) => (part ?? '').toString().trim().toLowerCase())
      .join('|');
    return `addr:${addrKey}`;
  }
  return `id:${site?.id ?? ''}`;
}

/**
 * Group one flat project's `sites` (SiteDetail[]) into the nested
 * Project → Site → Product view-model. Insertion order is preserved for both
 * sites and the products within each site.
 */
export function toProjectVm(project: any): ProjectVm {
  const siteDetails: any[] = Array.isArray(project?.sites) ? project.sites : [];
  const groups = new Map<string, SiteVm>();

  for (const detail of siteDetails) {
    const site = detail?.site ?? {};
    const key = siteGroupKey(site);

    let group = groups.get(key);
    if (!group) {
      group = {
        key,
        siteNumber: site?.siteNumber ?? null,
        site,
        products: [],
      };
      groups.set(key, group);
    }

    group.products.push({
      siteDetail: detail,
      site,
      proposal: detail?.proposal ?? {},
      reviewStatus: detail?.reviewStatus ?? '',
      // Absent visit MUST default to `null`, not `{}` like `proposal` above: the
      // progress stepper reads "a summary exists" as "this product has a site
      // visit", so an empty object would add two phantom site-visit stages to
      // every product in the app.
      siteVisit: detail?.siteVisit ?? null,
      status: detail?.proposal?.status ?? '',
      title: buildProductTitle(site),
    });
  }

  return {
    raw: project,
    projectId: project?.projectId ?? '',
    projectOrderId: project?.projectOrderId ?? '',
    rush: !!project?.rush,
    specialQuoteRequirements: project?.specialQuoteRequirements ?? '',
    tableA: project?.tableA,
    sites: Array.from(groups.values()),
  };
}

/** Map an array of flat projects (already page-filtered) to nested VMs. */
export function toProjectVms(projects: any[]): ProjectVm[] {
  return Array.isArray(projects) ? projects.map(toProjectVm) : [];
}
